import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Facebook, Instagram, Phone, MessageCircle, Link, Unlink,
  Loader2, AlertCircle, CheckCircle2, Share2
} from 'lucide-react';

const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;

const SocialTab = ({ socialConnections, onRefresh }) => {
  const { API, getAuthHeaders } = useAuth();
  const [connectingFacebook, setConnectingFacebook] = useState(false);
  const [facebookPages, setFacebookPages] = useState([]);
  const [whatsappAccounts, setWhatsappAccounts] = useState([]);
  const [showPagesDialog, setShowPagesDialog] = useState(false);
  const [tempAccessToken, setTempAccessToken] = useState(null);

  const fetchFacebookPages = useCallback(async (accessToken) => {
    try {
      const response = await axios.get(`${API}/social/pages`, {
        ...getAuthHeaders(),
        params: { access_token: accessToken }
      });
      const data = response.data;
      setFacebookPages(data.pages || data);
      setWhatsappAccounts(data.whatsapp_accounts || []);
      setShowPagesDialog(true);
    } catch (error) {
      console.error('Errore nel recupero pagine:', error);
      alert('Errore nel recupero delle pagine Facebook. Verifica che il token abbia i permessi corretti.');
    } finally {
      setConnectingFacebook(false);
    }
  }, [API, getAuthHeaders]);

  const handleFacebookLogin = useCallback(() => {
    if (!window.FB) {
      alert('Facebook SDK non caricato. Ricarica la pagina.');
      return;
    }
    setConnectingFacebook(true);
    window.FB.login((response) => {
      if (response.authResponse) {
        const accessToken = response.authResponse.accessToken;
        setTempAccessToken(accessToken);
        fetchFacebookPages(accessToken);
      } else {
        setConnectingFacebook(false);
      }
    }, {
      scope: 'pages_manage_posts,pages_read_engagement,pages_messaging,pages_read_user_content,pages_manage_metadata,instagram_basic,instagram_content_publish,instagram_manage_messages,pages_show_list,whatsapp_business_management,whatsapp_business_messaging,business_management'
    });
  }, [fetchFacebookPages]);

  const connectPage = async (page) => {
    try {
      await axios.post(`${API}/social/connect`, {
        platform: 'facebook', access_token: page.access_token,
        page_id: page.id, page_name: page.name
      }, getAuthHeaders());

      if (page.instagram_account) {
        await axios.post(`${API}/social/connect`, {
          platform: 'instagram', access_token: page.access_token,
          page_id: page.id, page_name: page.name,
          instagram_account_id: page.instagram_account.id,
          instagram_username: page.instagram_account.username
        }, getAuthHeaders());
      }
      setShowPagesDialog(false);
      setFacebookPages([]);
      setTempAccessToken(null);
      onRefresh();
      alert('Account collegati con successo!');
    } catch (error) {
      console.error('Errore nel collegamento:', error);
      alert('Errore nel collegamento dell\'account');
    }
  };

  const connectWhatsApp = async (waAccount, phoneNumber) => {
    try {
      await axios.post(`${API}/social/connect`, {
        platform: 'whatsapp', access_token: tempAccessToken,
        page_id: waAccount.id, page_name: waAccount.name,
        whatsapp_business_id: waAccount.id,
        whatsapp_phone_number_id: phoneNumber.id,
        whatsapp_phone_number: phoneNumber.display_phone_number,
        whatsapp_verified_name: phoneNumber.verified_name
      }, getAuthHeaders());
      setShowPagesDialog(false);
      setFacebookPages([]);
      setWhatsappAccounts([]);
      setTempAccessToken(null);
      onRefresh();
      alert('WhatsApp Business collegato con successo!');
    } catch (error) {
      console.error('Errore nel collegamento WhatsApp:', error);
      alert('Errore nel collegamento di WhatsApp Business');
    }
  };

  const disconnectAccount = async (connectionId) => {
    if (!window.confirm('Sei sicuro di voler disconnettere questo account?')) return;
    try {
      await axios.delete(`${API}/social/connections/${connectionId}`, getAuthHeaders());
      onRefresh();
    } catch (error) {
      console.error('Errore nella disconnessione:', error);
    }
  };

  // Load Facebook SDK
  useEffect(() => {
    if (!FACEBOOK_APP_ID || window.FB) return;
    window.fbAsyncInit = function () {
      window.FB.init({ appId: FACEBOOK_APP_ID, cookie: true, xfbml: true, version: 'v22.0' });
    };
    (function (d, s, id) {
      let js; const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/it_IT/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));
  }, []);

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook': return <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center"><Facebook className="w-5 h-5 text-white" /></div>;
      case 'instagram': return <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center"><Instagram className="w-5 h-5 text-white" /></div>;
      case 'whatsapp': return <div className="w-10 h-10 rounded-lg bg-green-600 flex items-center justify-center"><Phone className="w-5 h-5 text-white" /></div>;
      default: return <div className="w-10 h-10 rounded-lg bg-gray-600 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-white" /></div>;
    }
  };

  const getDisplayName = (conn) => {
    if (conn.platform === 'instagram') return `@${conn.instagram_username}`;
    if (conn.platform === 'whatsapp') return conn.whatsapp_phone_number || conn.page_name;
    return conn.page_name;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Connessioni Social & WhatsApp
          </CardTitle>
          <CardDescription>Collega Facebook, Instagram e WhatsApp Business con un solo login Meta per pubblicare e messaggiare dalla piattaforma</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {socialConnections.length > 0 && (
            <div className="space-y-3">
              <Label>Account Collegati</Label>
              {socialConnections.map((conn) => (
                <div key={conn.id} className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-3">
                    {getPlatformIcon(conn.platform)}
                    <div>
                      <p className="font-medium">{getDisplayName(conn)}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        {conn.platform === 'whatsapp' ? `WhatsApp Business - ${conn.whatsapp_verified_name || 'Connesso'}` : 'Connesso'}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => disconnectAccount(conn.id)} className="text-destructive hover:text-destructive">
                    <Unlink className="w-4 h-4 mr-1" />Disconnetti
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <Button onClick={handleFacebookLogin} disabled={connectingFacebook} className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto" data-testid="connect-facebook-btn">
              {connectingFacebook ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connessione in corso...</>
              ) : (
                <><Facebook className="w-4 h-4 mr-2" />Accedi con Facebook</>
              )}
            </Button>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>Un unico login sblocca:</span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-700 dark:text-blue-400">
                <Facebook className="w-3 h-3" /> Pagine Facebook
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-700 dark:text-pink-400">
                <Instagram className="w-3 h-3" /> Instagram Business
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                <MessageCircle className="w-3 h-3" /> WhatsApp Business
              </span>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <div className="flex gap-3">
              <MessageCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-emerald-700 dark:text-emerald-400">WhatsApp Business incluso</p>
                <p className="text-muted-foreground mt-1">
                  Se il tuo account Meta Business ha un numero WhatsApp Business configurato, verrà proposto per il collegamento nella stessa finestra di selezione — non serve un login separato.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-700 dark:text-amber-400">Requisiti per Instagram</p>
                <p className="text-muted-foreground mt-1">
                  Per pubblicare su Instagram, il tuo account deve essere di tipo Business o Creator e collegato a una Pagina Facebook.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pages Selection Dialog */}
      <Dialog open={showPagesDialog} onOpenChange={setShowPagesDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">Seleziona Account da Collegare</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {facebookPages.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-blue-600" />Pagine Facebook
                </h4>
                <p className="text-xs text-muted-foreground">Se la pagina ha un account Instagram Business collegato, verrà connesso automaticamente.</p>
                <div className="space-y-2">
                  {facebookPages.map((page) => (
                    <div key={page.id} className="p-4 rounded-lg border hover:border-primary cursor-pointer transition-all" onClick={() => connectPage(page)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center"><Facebook className="w-5 h-5 text-white" /></div>
                          <div>
                            <p className="font-medium">{page.name}</p>
                            <p className="text-xs text-muted-foreground">Pagina Facebook</p>
                          </div>
                        </div>
                        <Link className="w-4 h-4 text-muted-foreground" />
                      </div>
                      {page.instagram_account && (
                        <div className="mt-3 pt-3 border-t flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center"><Instagram className="w-4 h-4 text-white" /></div>
                          <div>
                            <p className="text-sm font-medium">@{page.instagram_account.username}</p>
                            <p className="text-xs text-muted-foreground">Instagram Business collegato</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {whatsappAccounts.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-600" />Account WhatsApp Business
                </h4>
                <div className="space-y-2">
                  {whatsappAccounts.map((wa) => (
                    <div key={wa.id} className="p-4 rounded-lg border space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-600 flex items-center justify-center"><Phone className="w-5 h-5 text-white" /></div>
                        <div>
                          <p className="font-medium">{wa.name}</p>
                          <p className="text-xs text-muted-foreground">{wa.business_name}</p>
                        </div>
                      </div>
                      {wa.phone_numbers && wa.phone_numbers.length > 0 && (
                        <div className="pl-12 space-y-1">
                          <p className="text-xs text-muted-foreground">Numeri disponibili:</p>
                          {wa.phone_numbers.map((phone) => (
                            <div key={phone.id} className="flex items-center justify-between p-2 rounded bg-muted/50 hover:bg-muted cursor-pointer" onClick={() => connectWhatsApp(wa, phone)}>
                              <div>
                                <p className="text-sm font-medium">{phone.display_phone_number}</p>
                                <p className="text-xs text-muted-foreground">{phone.verified_name}</p>
                              </div>
                              <Link className="w-4 h-4 text-muted-foreground" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {facebookPages.length === 0 && whatsappAccounts.length === 0 && (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">Nessun account trovato</p>
                <p className="text-sm text-muted-foreground mt-1">Assicurati di avere i permessi di amministratore su almeno una Pagina Facebook o un account WhatsApp Business</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialTab;
